import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip
} from '@mui/material';
import { format, startOfDay, endOfDay } from 'date-fns';
import { getAvatarSrc } from '../../utils/avatarConstants';

/**
 * Modal that displays all bookings and events for a selected date in a day-view style.
 * Used when clicking a day cell in the mobile week or calendar view.
 */
function DayBookingsModal({ open, onClose, date, events = [], onEventClick, getStatusColorForChip }) {
  if (!date) return null;

  const dayStart = startOfDay(new Date(date));
  const dayEnd = endOfDay(new Date(date));

  const dayEvents = events.filter((event) => {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);
    return eventStart < dayEnd && eventEnd > dayStart;
  });

  const sortedEvents = [...dayEvents].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );

  const buildTimeLabel = (event) => {
    if (event.allDay) return 'All day';
    const startLabel = event.start ? format(new Date(event.start), 'h:mm a') : '';
    const endLabel = event.end ? format(new Date(event.end), 'h:mm a') : '';
    return startLabel && endLabel ? `${startLabel} - ${endLabel}` : startLabel || endLabel || '—';
  };

  const getClientName = (event) => {
    const props = event.extendedProps || {};
    if (props.type === 'booking' && props.booking?.client) {
      const c = props.booking.client;
      return [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Client';
    }
    if (props.type === 'event' && props.event?.title) return props.event.title;
    return event.title || '—';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" component="span">
          {format(new Date(date), 'EEEE, d MMMM yyyy')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {sortedEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No bookings or events on this day
          </Typography>
        ) : (
          <List disablePadding>
            {sortedEvents.map((event) => {
              const props = event.extendedProps || {};
              const isBooking = props.type === 'booking';
              const booking = props.booking;
              const status = props.status || booking?.status;
              const serviceName = isBooking ? (booking?.serviceType?.name || 'Appointment') : event.title;
              const avatarSrc = isBooking && booking?.client ? getAvatarSrc(booking.client) : null;
              const initials = isBooking && booking?.client
                ? [booking.client.firstName, booking.client.lastName]
                  .filter(Boolean)
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2) || 'CL'
                : 'EV';

              return (
                <ListItem
                  key={event.id}
                  onClick={() => onEventClick && onEventClick(event)}
                  sx={{
                    px: 0,
                    py: 1.5,
                    alignItems: 'flex-start',
                    borderLeft: '4px solid',
                    borderColor: event.borderColor || 'divider',
                    borderRadius: 1,
                    bgcolor: event.backgroundColor || 'action.hover',
                    mb: 1,
                    cursor: onEventClick ? 'pointer' : 'default',
                    touchAction: 'manipulation',
                    minWidth: 0
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar
                      src={avatarSrc}
                      sx={{
                        width: 40,
                        height: 40,
                        fontSize: '0.875rem',
                        bgcolor: event.borderColor || 'primary.main'
                      }}
                    >
                      {initials}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ minWidth: 0 }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {getClientName(event)}
                        </Typography>
                        {status && (
                          <Chip
                            size="small"
                            label={status}
                            color={(getStatusColorForChip && getStatusColorForChip(status)) || 'default'}
                            sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary" component="span" display="block">
                          {serviceName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {buildTimeLabel(event)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DayBookingsModal;
